import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle, FileText, ExternalLink, Download, Upload, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import ToolLayout from '../components/ToolLayout';
import { parseBinderExportFile, BINDER_EXPORT_EXTENSION } from '../lib/binderExportFormat';
import { getEmergencyPlanSectionDescriptors } from '../lib/binderSections/emergencyPlan';
import { budgetExportToSections } from '../lib/binderSections/budget';
import { assembleBinderBody, wrapInHtmlDocument } from '../lib/binderAssembly';
import { toast } from 'sonner';

const BINDER_SECTIONS = [
  { id: 'risk', label: 'Location risk assessment', note: 'Provided by Wahle-Tec' },
  { id: 'emergency-plan', label: 'Emergency plan (family, evacuation, disasters, communication, etc.)', toolPath: '/emergency-plan', toolLabel: 'Emergency Plan' },
  { id: 'contacts', label: 'Emergency contact sheets', toolPath: '/emergency', toolLabel: 'Emergency Contacts' },
  { id: 'budget', label: 'Budget summary', toolPath: '/budget', toolLabel: 'Budget Tool' },
  { id: 'debt', label: 'Debt overview', toolPath: '/debt-test', toolLabel: 'Debt Tool' },
  { id: 'maintenance', label: 'Maintenance schedule', toolPath: '/maintenance', toolLabel: 'Maintenance' },
  { id: 'inventory', label: 'Supply / kit inventory', toolPath: '/inventory', toolLabel: 'Inventory' },
  { id: 'documents', label: 'Document audit, task list & pet ID', toolPath: '/documents', toolLabel: 'Important Documents' },
  { id: 'map-index', label: 'Map index (curated resources + instructions)', toolPath: '/map-index', toolLabel: 'Map Index' },
  { id: 'financial-summary', label: 'Financial & insurance summary', toolPath: '/financial-summary', toolLabel: 'Financial Summary' },
  { id: 'valuables', label: 'Valuables inventory', toolPath: '/valuables', toolLabel: 'Valuables' },
  { id: 'babysitter', label: 'Babysitter quick reference', toolPath: '/babysitter-reference', toolLabel: 'Babysitter Reference' }
];

let nextId = 0;
function generateSectionId() {
  return `section-${++nextId}-${Date.now()}`;
}

const BinderHub = () => {
  const [sections, setSections] = useState([]);

  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const readFile = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve({ file, text: reader.result });
        reader.onerror = () => reject(new Error(`Failed to read ${file.name}`));
        reader.readAsText(file, 'utf8');
      });
    Promise.all(files.map(readFile))
      .then((results) => {
        const newSections = [];
        results.forEach(({ file, text }) => {
          try {
            const data = JSON.parse(text);
            if (data != null && typeof data === 'object') {
              const isBinderFormat = typeof data.version === 'number' && typeof data.toolId === 'string' && Array.isArray(data.sections);
              if (isBinderFormat) {
                const parsed = parseBinderExportFile(text, file.name);
                parsed.sections.forEach((s) => {
                  newSections.push({
                    id: generateSectionId(),
                    sourceFile: file.name,
                    toolName: parsed.toolName,
                    name: s.name,
                    content: s.content
                  });
                });
              } else if (data.family_info !== undefined || data.evacuation_plans !== undefined || data.disaster_plans !== undefined) {
                const descriptors = getEmergencyPlanSectionDescriptors(data);
                const sections = descriptors.filter((d) => d.hasContent).map((d) => ({ name: d.name, content: d.getContent() }));
                sections.forEach((s) => {
                  newSections.push({
                    id: generateSectionId(),
                    sourceFile: file.name,
                    toolName: 'Emergency Plan',
                    name: s.name,
                    content: s.content
                  });
                });
              } else if (data.budget_info !== undefined) {
                const sections = budgetExportToSections(data);
                sections.forEach((s) => {
                  newSections.push({
                    id: generateSectionId(),
                    sourceFile: file.name,
                    toolName: 'Budget Tool',
                    name: s.name,
                    content: s.content
                  });
                });
              } else {
                toast.error(`${file.name}: unrecognized format. Use an export from a tool.`);
              }
            } else {
              toast.error(`${file.name}: invalid JSON`);
            }
          } catch (err) {
            toast.error(`Could not read ${file.name}: ${err.message}`);
          }
        });
        if (newSections.length > 0) {
          setSections((prev) => [...prev, ...newSections]);
        }
      })
      .catch((err) => toast.error(err.message));
    e.target.value = '';
  }, []);

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sections.length) return;
    setSections((prev) => {
      const next = [...prev];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
  };

  const removeSection = (id) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const setSectionName = (id, name) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, name: name.trim() || s.name } : s)));
  };

  const handleCompileBinder = () => {
    if (sections.length === 0) {
      toast.error('Add at least one exported file before generating the binder.');
      return;
    }
    const descriptors = sections.map((s) => ({
      name: s.name,
      hasContent: true,
      getContent: () => s.content
    }));
    const binderContent = assembleBinderBody(descriptors, {
      title: 'Preparedness Binder',
      subtitle: 'Complete Preparedness Binder',
      preparedBy: '[Your Name]'
    });
    const htmlDocument = wrapInHtmlDocument(binderContent, { title: 'Preparedness Binder' });
    const blob = new Blob([htmlDocument], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Preparedness_Binder_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Binder downloaded. Open the HTML file in your browser to view or print.');
  };

  return (
    <ToolLayout
      title="Build your binder"
      icon="📚"
      guide={{
        intro: 'This is where it all comes together — combine exports from every tool into one printable binder.',
        steps: [
          'Upload .binder.json export files from any of the tools above.',
          'Reorder, rename, or remove sections as you like.',
          'Click Generate to create one HTML document with cover page and table of contents.',
          'Print or save the binder.',
        ],
      }}
    >
      <div className="space-y-6 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Binder checklist
            </CardTitle>
            <CardDescription>
              Complete each tool and export its product to your computer. Then upload those files here, arrange the sections as you like, and generate your binder.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {BINDER_SECTIONS.map((section) => (
              <div
                key={section.id}
                className="flex items-center justify-between gap-4 p-3 rounded-lg border bg-card hover:bg-muted/30"
              >
                <span className="font-medium">{section.label}</span>
                {section.toolPath ? (
                  <Link
                    to={section.toolPath + (section.toolPathHash || '')}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    {section.toolLabel}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                ) : (
                  <span className="text-sm text-muted-foreground">{section.note}</span>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              Upload exports
            </CardTitle>
            <CardDescription>
              Upload one or more export files from the tools above (e.g. {BINDER_EXPORT_EXTENSION} or emergency plan backup JSON). Sections from all files will be merged into one list.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/30">
              <Upload className="h-8 w-8 text-muted-foreground mb-2" />
              <span className="text-sm text-muted-foreground">Click to select files or drag and drop</span>
              <input
                type="file"
                className="hidden"
                accept={BINDER_EXPORT_EXTENSION}
                multiple
                onChange={handleFileChange}
              />
            </label>
          </CardContent>
        </Card>

        {sections.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Organize sections</CardTitle>
              <CardDescription>
                Reorder with the arrows, remove sections you don&apos;t want, or edit section titles for the table of contents. Order here is the order in your final binder.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {sections.map((section, index) => (
                <div
                  key={section.id}
                  className="flex items-center gap-2 p-3 rounded-lg border bg-card"
                >
                  <div className="flex flex-col gap-0.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveSection(index, -1)}
                      disabled={index === 0}
                      aria-label="Move up"
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => moveSection(index, 1)}
                      disabled={index === sections.length - 1}
                      aria-label="Move down"
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <Input
                    value={section.name}
                    onChange={(e) => setSectionName(section.id, e.target.value)}
                    className="flex-1 font-medium"
                  />
                  {section.toolName && (
                    <span className="text-xs text-muted-foreground shrink-0 max-w-[120px] truncate" title={section.toolName}>
                      {section.toolName}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => removeSection(section.id)}
                    aria-label="Remove section"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-primary" />
              Generate binder
            </CardTitle>
            <CardDescription>
              Build one document with a cover, table of contents, and all sections in the order you chose. You get a single HTML file to open in your browser and print.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={handleCompileBinder}
              className="w-full sm:w-auto gap-2"
              disabled={sections.length === 0}
            >
              <Download className="h-4 w-4" />
              Generate binder
            </Button>
            {sections.length === 0 && (
              <p className="text-sm text-muted-foreground mt-2">
                Upload at least one export file above, then click to generate.
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground">Tip</p>
                <p className="text-muted-foreground">
                  Store your printed binder in a fireproof, waterproof safe. Review it at least once a year and ensure household members know where it is.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </ToolLayout>
  );
};

export default BinderHub;
